import { createSelector, createSlice } from "@reduxjs/toolkit";
import {
  GRID_VIDEO_ASPECT_RATIO,
  GRID_VIDEO_MARGIN,
  MAX_CONCURRENT_VIDEOS,
  MEDIA_LOOP_INTERVAL,
  SPACE_SIZE_VALUES,
} from "constants/constants";
import { gatherAnyInRangeChanges } from "space/spaceMediaPhysics";
import {
  graphicRemoteJoined,
  graphicRemoteLeft,
  graphicUpdateDevices,
} from "space/spaceSignalling";
import {
  startTest,
  testingMyMovement,
  testingOtherMovement,
  testStarted,
} from "space/spaceTest";
import { gatherAnyDeviceChanges } from "./mediaServerSlice";
import { selectInfoPage, setInfoPage } from "./textSlice";
import {
  gatherAnyLeftPeopleChanges,
  gatherAnyNewPeopleChanges,
  selectGotMyBearings,
  turnOffFocus,
} from "./spaceSlice";
import { makeMyTease } from "space/spaceBoard";
let dispatch = null;
let getState = null;
import("Store").then((module) => {
  dispatch = module.store.dispatch;
  getState = module.store.getState;
});

// Space Side Bar Types
export const SPACE_PEOPLE = "SPACE_PEOPLE";
export const SPACE_CHAT = "SPACE_CHAT";

// Layout Names
export const MY_BOARD = "MY_BOARD";
export const SPACE_LAYOUT = "SPACE_LAYOUT";
export const GRID_LAYOUT = "GRID_LAYOUT";
export const PINNED_LAYOUT = "PINNED_LAYOUT";

// Grid and Pinned Layout View Type
export const SHARE = "SHARE";
export const CAMERA = "CAMERA";

// Media Container Views
export const PINNED_CAMERA = "PINNED_CAMERA";
export const PINNED_SHARE = "PINNED_SHARE";
export const GRID_VIEW = "GRID_VIEW";
export const SIDE_LARGE_VIEW = "SIDE_LARGE_VIEW";
export const SIDE_LARGE_EMPTY = "SIDE_LARGE_EMPTY";
export const SIDE_SMALL_VIEW = "SIDE_SMALL_VIEW";
export const SIDE_MINI_VIEW = "SIDE_MINI_VIEW";

const INITIAL_STATE = {
  // Room variables
  room_url: null,
  membersMuted: false,
  membersShoutable: true,
  membersChatable: true,
  membersLocked: false,

  // Local Room State
  spaceStartTime: null,
  spaceBoxActive: false,
  spaceSideBar: null,
  adminControlsActive: false,

  // My values
  myLuid: null,
  meAdmin: false,
  meMuted: false,
  meShoutable: true,
  meShout: false,
  mePinnedShare: false,
  meLocked: false,
  meApp: null,
  meFocus: false,
  mePreFocus: { mic: false, cam: false, screen: false },
  meBuzzed: 0,

  // Regularly Changing
  people: {},
  numPeople: 1,
  spaceLayout: { value: SPACE_LAYOUT },
  firstVidLuid: null,
  runningVideos: 0,
  smallLuids: [],
  bigLuids: [],
  spaceSize: 1,
};

export const slice = createSlice({
  name: "spaceMedia",
  initialState: INITIAL_STATE,
  reducers: {
    setSpaceInitPack: (state, action) => {
      state.room_url = action.payload.room_url;
      state.spaceStartTime = Date.now();
      state.membersMuted = action.payload.membersMuted;
      state.membersShoutable = action.payload.membersShoutable;
      state.membersChatable = action.payload.membersChatable;
      state.membersLocked = action.payload.membersLocked;
    },
    setMePerson: (state, action) => {
      let me = action.payload;
      state.myLuid = me.luid;
      state.meAdmin = me.admin;
      state.meShoutable = me.shoutable;
      state.meShout = me.shout;
      state.meFocus = me.focus;
      state.meMuted = me.muted;
      state.meLocked = me.locked;
      state.meBuzzed = me.buzzed;
    },
    setMeAdmin: (state, action) => {
      state.meAdmin = action.payload;
    },
    setMeMuted: (state, action) => {
      state.meMuted = action.payload;
    },
    setMeShoutable: (state, action) => {
      state.meShoutable = action.payload;
    },
    setMeShout: (state, action) => {
      state.meShout = action.payload;
    },
    setMePinnedShare: (state, action) => {
      state.mePinnedShare = action.payload;
    },
    setMeFocus: (state, action) => {
      state.meFocus = action.payload.value;
      if (action.payload.mePreFocus)
        state.mePreFocus = action.payload.mePreFocus;
      else state.mePreFocus = { mic: false, cam: false, screen: false };
    },
    setMeLocked: (state, action) => {
      state.meLocked = action.payload.value;
    },
    setMeBuzzed: (state, action) => {
      state.meBuzzed = action.payload;
    },
    setMutedForMe: (state, action) => {
      state.people[action.payload.luid].mutedForMe = action.payload.value;
    },
    setMembersMuted: (state, action) => {
      state.membersMuted = action.payload;
      if (!state.meAdmin) state.meMuted = action.payload;
      for (let luid in state.people)
        if (luid !== state.myLuid && !state.people[luid].admin)
          state.people[luid].muted = action.payload;
    },
    setMembersShoutable: (state, action) => {
      state.membersShoutable = action.payload;
      if (!state.meAdmin) {
        state.meShoutable = action.payload;
        if (!action.payload) {
          state.meShout = action.payload;
          state.mePinnedShare = action.payload;
        }
      }
      for (let luid in state.people)
        if (luid !== state.myLuid && !state.people[luid].admin)
          state.people[luid].shoutable = action.payload;
    },
    setMembersChatable: (state, action) => {
      state.membersChatable = action.payload;
    },
    setMembersLocked: (state, action) => {
      state.membersLocked = action.payload;
      if (!state.meAdmin) state.meLocked = action.payload;
      for (let luid in state.people)
        if (luid !== state.myLuid && !state.people[luid].admin)
          state.people[luid].locked = action.payload;
    },
    setSpaceBoxActive: (state, action) => {
      state.spaceBoxActive = action.payload;
      if (!action.payload) {
        state.spaceSideBar = null;
      }
    },
    setSpaceSidebar: (state, action) => {
      state.spaceSideBar = action.payload;
    },
    setAdminControlsActive: (state, action) => {
      state.adminControlsActive = action.payload;
    },
    setRemoteMuted: (state, action) => {
      state.people[action.payload.luid].muted = action.payload.value;
    },
    setRemoteShoutable: (state, action) => {
      state.people[action.payload.luid].shoutable = action.payload.value;
    },
    setRemoteLocked: (state, action) => {
      state.people[action.payload.luid].locked = action.payload.value;
    },
    setRemoteAdmin: (state, action) => {
      state.people[action.payload.luid].admin = action.payload.value;
    },
    setSpaceChanges: (state, action) => {
      let {
        deviceChanges,
        inRangeArray,
        newPeople,
        leftPeople,
        spaceLayoutPreference,
      } = action.payload;
      let people = state.people;
      window.efs_debug && window.log("Set Space Changes");
      window.efs_debug &&
        window.log(JSON.parse(JSON.stringify(action.payload)));

      // debugger;
      // Handling Joins
      for (let luid in newPeople) {
        graphicRemoteJoined(newPeople[luid]);
        people[luid] = newPeople[luid];
        graphicUpdateDevices(
          luid,
          newPeople[luid].micOn,
          newPeople[luid].share,
          newPeople[luid].camOn
        );
      }
      // Handling Device Changes
      for (let i in deviceChanges) {
        if (people[i]) {
          people[i] = { ...people[i], ...deviceChanges[i] };
          graphicUpdateDevices(
            i,
            people[i].micOn,
            people[i].share,
            people[i].camOn
          );
        }
      }

      // Handling Leaves
      for (let luid in leftPeople) {
        graphicRemoteLeft(luid);
        delete people[luid];
      }

      // Handling InRanges
      if (inRangeArray)
        inRangeArray.forEach((el) => {
          if (people[el.luid]) {
            people[el.luid].inRange = el.inRange;
            people[el.luid].time = el.time;
            people[el.luid].shout = el.shout;
            people[el.luid].pinnedShare = el.pinnedShare;
          }
        });

      let tempLayout = spaceLayoutPreference
        ? spaceLayoutPreference
        : state.spaceLayout;

      let mediaQueue = [];
      let spaceStartTime = state.spaceStartTime;
      let videosOnSide = 0; // not to be confused with runningVideos later on

      // Determining orders
      let numPeople = 1;
      for (let luid in people) {
        numPeople++;
        let { camOn, share, micOn, time, shout, pinnedShare } = people[luid];
        if (time) {
          if (camOn || share) videosOnSide++;

          let order =
            (shout || pinnedShare) && (camOn || share)
              ? time - spaceStartTime + 0
              : shout || pinnedShare
              ? time - spaceStartTime + 200000000
              : camOn || share
              ? time - spaceStartTime + 400000000
              : micOn
              ? time - spaceStartTime + 600000000
              : time - spaceStartTime + 800000000;

          // 2^ 31 - 1 is MAX for flex-box order property = 2 147 483 647 is max

          if (order) {
            people[luid].order = order;

            // Tagging Luid to end of Order so i can extract it later
            order = order * 1000 + parseInt(luid);
            mediaQueue.splice(locationOf(order, mediaQueue) + 1, 0, order);
          }
        }
      }

      // When people have left my range and I am in pinned or grid view
      // Needs to be done before determing other views
      if (videosOnSide === 0 && tempLayout.value !== MY_BOARD)
        tempLayout = { value: SPACE_LAYOUT };

      let bigLuids = [];
      let smallLuids = [];
      let runningVideos = 0;
      let firstVidLuid = null;

      // Pinned layout takes priority over others
      // therefore needs to be accounted for before iterating
      // over the mediaqueue
      if (tempLayout.value === PINNED_LAYOUT) {
        let pPerson = people[tempLayout.luid];
        if (!pPerson || (!pPerson.share && !pPerson.camOn) || !pPerson.time) {
          tempLayout = { value: SPACE_LAYOUT };
        } else {
          runningVideos++;
          firstVidLuid = tempLayout.luid;
          bigLuids.push(tempLayout.luid);
          if (
            (!pPerson.share && tempLayout.media === SHARE && pPerson.camOn) ||
            (pPerson.camOn &&
              tempLayout.media === CAMERA &&
              !(!pPerson.inRange && pPerson.pinnedShare))
          ) {
            tempLayout = {
              value: PINNED_LAYOUT,
              luid: pPerson.luid,
              media: CAMERA,
            };
            people[tempLayout.luid].visual = PINNED_CAMERA;
          } else if (
            (!pPerson.camOn && tempLayout.media === CAMERA && pPerson.share) ||
            (pPerson.share && tempLayout.media === SHARE)
          ) {
            tempLayout = {
              value: PINNED_LAYOUT,
              luid: pPerson.luid,
              media: SHARE,
            };
            people[tempLayout.luid].visual = PINNED_SHARE;
          } else if (
            !pPerson.inRange &&
            pPerson.pinnedShare &&
            tempLayout.media === CAMERA &&
            pPerson.share
          ) {
            tempLayout = {
              value: PINNED_LAYOUT,
              luid: pPerson.luid,
              media: SHARE,
            };
          }
        }
      }

      mediaQueue.forEach((order) => {
        let luid = (order % 1000).toString();
        let videoable = false;
        let visual = null;

        // Don't re-add Pinned Value;
        if (tempLayout.value === PINNED_LAYOUT && tempLayout.luid === luid) {
          // debugger;
          return;
        }

        // Counting running videos so we keep max videos limit
        if (people[luid].camOn || people[luid].share) {
          // Always knowing what the first vid luid is
          if (firstVidLuid === null) firstVidLuid = luid;
          runningVideos++;
          if (runningVideos <= MAX_CONCURRENT_VIDEOS) videoable = true;
        }

        // Deciding the media container layout
        if (
          tempLayout.value === SPACE_LAYOUT ||
          tempLayout.value === MY_BOARD
        ) {
          smallLuids.push(luid);
          if (videoable) visual = SIDE_LARGE_VIEW;
          else if (people[luid].camOn || people[luid].share)
            visual = SIDE_MINI_VIEW;
          else visual = SIDE_SMALL_VIEW;
        } else if (tempLayout.value === GRID_LAYOUT) {
          if (videoable) {
            visual = GRID_VIEW;
            bigLuids.push(luid);
          } else {
            visual = SIDE_MINI_VIEW;
            smallLuids.push(luid);
          }
        } else if (tempLayout.value === PINNED_LAYOUT) {
          // Pinned has already been added, so we add others now
          smallLuids.push(luid);
          if (videoable) visual = SIDE_LARGE_VIEW;
          else if (people[luid].camOn || people[luid].share)
            visual = SIDE_MINI_VIEW;
          else visual = SIDE_SMALL_VIEW;
        }
        people[luid].visual = visual;
      });
      if (
        tempLayout.value === MY_BOARD ||
        tempLayout.value === PINNED_LAYOUT ||
        tempLayout.value === GRID_LAYOUT
      ) {
        state.spaceBoxActive = true;
      }
      state.spaceLayout = tempLayout;
      state.runningVideos = runningVideos;
      state.firstVidLuid = firstVidLuid;
      state.smallLuids = smallLuids;
      state.bigLuids = bigLuids;
      state.numPeople = numPeople;
      state.spaceSize = determineSpaceSize(numPeople);
    },
    refreshSpaceMediaSlice: (state) => {
      return { ...INITIAL_STATE, spaceBoxActive: true };
    },
    resetSpaceMediaSlice: (state) => INITIAL_STATE,
  },
});

export const {
  setSpaceInitPack,

  setSpaceBoxActive,
  setSpaceSidebar,
  setAdminControlsActive,

  setMePerson,
  setMeAdmin,
  setMeMuted,
  setMeShoutable,
  setMeLocked,
  setMeShout,
  setMePinnedShare,
  setMeFocus,
  setMeBuzzed,

  setMutedForMe,
  setMembersMuted,
  setMembersShoutable,
  setMembersChatable,
  setMembersLocked,
  setRemoteMuted,
  setRemoteShoutable,
  setRemoteLocked,
  setRemoteAdmin,

  setSpaceChanges,
  refreshSpaceMediaSlice,
  resetSpaceMediaSlice,
} = slice.actions;

// Direct Selectors
export const selectMembersMuted = (state) => state.spaceMedia.membersMuted;
export const selectMembersShoutable = (state) =>
  state.spaceMedia.membersShoutable;
export const selectMembersChatable = (state) =>
  state.spaceMedia.membersChatable;
export const selectMembersLocked = (state) => state.spaceMedia.membersLocked;

export const selectSpaceBoxActive = (state) => state.spaceMedia.spaceBoxActive;
export const selectSpaceSideBar = (state) => state.spaceMedia.spaceSideBar;
export const selectAdminControlsActive = (state) =>
  state.spaceMedia.adminControlsActive;

export const selectMyLuid = (state) => state.spaceMedia.myLuid;
export const selectMeAdmin = (state) => state.spaceMedia.meAdmin;
export const selectMeMuted = (state) => state.spaceMedia.meMuted;
export const selectMeShoutable = (state) => state.spaceMedia.meShoutable;
export const selectMeShout = (state) => state.spaceMedia.meShout;
export const selectMePinnedShare = (state) => state.spaceMedia.mePinnedShare;
export const selectMeLocked = (state) => state.spaceMedia.meLocked;
export const selectMeFocus = (state) => state.spaceMedia.meFocus;
export const selectMePreFocus = (state) => state.spaceMedia.mePreFocus;

export const selectPeople = (state) => state.spaceMedia.people;
export const selectSpaceLayout = (state) => state.spaceMedia.spaceLayout;
export const selectFirstVidLuid = (state) => state.spaceMedia.firstVidLuid;
export const selectRunningVideos = (state) => state.spaceMedia.runningVideos;
export const selectSmallLuids = (state) => state.spaceMedia.smallLuids;
export const selectBigLuids = (state) => state.spaceMedia.bigLuids;
export const selectNumPeople = (state) => state.spaceMedia.numPeople;
export const selectSpaceSize = (state) => state.spaceMedia.spaceSize;

// Indirect Selectors
export const selectSpaceLayoutValue = (state) =>
  state.spaceMedia.spaceLayout.value;

// Custom Selectors
export const selectBigVideoWidth = createSelector(
  (state) => state.spaceMedia.bigLuids.length,
  (state) => state.general.gridWidth,
  (state) => state.general.gridHeight,
  (numBigs, gridWidth, gridHeight) => {
    if (numBigs === 0) return 0;
    // let elem = document.getElementById(MEDIA_ARRAY);
    // if (elem) elem.scrollTop = 0;
    return Dish(numBigs, gridWidth, gridHeight);
  }
);

// Selector Creators
// ***

//#region Helper Functions

function determineSpaceSize(pop) {
  if (pop < SPACE_SIZE_VALUES[2].pop) return 1;
  else if (pop < SPACE_SIZE_VALUES[3].pop) return 2;
  else if (pop < SPACE_SIZE_VALUES[4].pop) return 3;
  else return 4;
}

function locationOf(element, array, start, end) {
  start = start || 0;
  end = end || array.length;
  var pivot = parseInt(start + (end - start) / 2, 10);
  if (array[pivot] === element) return pivot;
  if (end - start <= 1) return array[pivot] > element ? pivot - 1 : pivot;
  if (array[pivot] < element) {
    return locationOf(element, array, pivot, end);
  } else {
    return locationOf(element, array, start, pivot);
  }
}

function Area(testWidth, Count, GridWidth, GridHeight, Margin = 10) {
  let i = 0;
  let accWidth = 0;
  let accHeight = testWidth * GRID_VIDEO_ASPECT_RATIO + Margin * 2;
  while (i < Count) {
    if (accWidth + testWidth > GridWidth) {
      accWidth = 0;
      accHeight = accHeight + testWidth * GRID_VIDEO_ASPECT_RATIO + Margin * 2;
    }
    accWidth = accWidth + testWidth + Margin * 2;
    i++;
  }
  if (accHeight > GridHeight || testWidth > GridWidth) return true;
  else return testWidth;
}

// Dish:
export function Dish(numBigs, gridWidth, gridHeight) {
  // variables:
  // debugger;
  let GridWidth = gridWidth - GRID_VIDEO_MARGIN * 2;
  let GridHeight = gridHeight - GRID_VIDEO_MARGIN * 2;
  let maxContainerWidth = 0;

  // loop (i recommend you optimize this)
  let testWidth = 0;
  while (testWidth < 5000) {
    let tooBig = Area(
      testWidth,
      numBigs,
      GridWidth,
      GridHeight,
      GRID_VIDEO_MARGIN
    );
    if (tooBig === true) {
      maxContainerWidth = testWidth - 1;
      break;
    }
    testWidth = testWidth + 0.5;
  }

  return maxContainerWidth - GRID_VIDEO_MARGIN * 2;
}

//#endregion

//#region CHANGING SPACEBOX ACTIVE and PEOPLE

// remember last one
let lastLayoutSetting = null;

export function hideSpaceBox() {
  if (selectSpaceBoxActive(getState())) {
    lastLayoutSetting = selectSpaceLayout(getState());
    openToSpaceLayout();
    dispatch(setSpaceBoxActive(false));
  }
}

export function showSpaceBox() {
  if (!selectSpaceBoxActive(getState())) {
    if (selectInfoPage(getState())) dispatch(setInfoPage(false));
    if (lastLayoutSetting) {
      if (lastLayoutSetting.value === GRID_LAYOUT) {
        manualSetSpaceLayout({ value: GRID_LAYOUT });
      } else if (lastLayoutSetting.value === PINNED_LAYOUT) {
        let person = selectPeople(getState())[lastLayoutSetting.luid];
        if (person) {
          if (lastLayoutSetting.media === CAMERA) {
            if (person.camOn) manualSetSpaceLayout(lastLayoutSetting);
            else if (person.share)
              manualSetSpaceLayout({
                value: PINNED_LAYOUT,
                luid: lastLayoutSetting.luid,
                media: SHARE,
              });
          } else if (lastLayoutSetting.media === SHARE) {
            if (person.share) manualSetSpaceLayout(lastLayoutSetting);
            else if (person.camOn)
              manualSetSpaceLayout({
                value: PINNED_LAYOUT,
                luid: lastLayoutSetting.luid,
                media: CAMERA,
              });
          }
        }
      }
    }
    lastLayoutSetting = null;
    dispatch(setSpaceBoxActive(true));
  }
}

export function toggleSpaceBox() {
  if (selectSpaceBoxActive(getState())) hideSpaceBox();
  else showSpaceBox();
}

//#endregion

//#region CHANGING SPACE LAYOUT

let spaceLayoutPreference = null;
export function manualSetSpaceLayout(spaceLayout) {
  spaceLayoutPreference = spaceLayout;
  forceHandleSpaceChanges();
}

export function gatherAnySpaceLayoutPreferenceChanges() {
  if (!spaceLayoutPreference) return null;
  let pref = { ...spaceLayoutPreference };
  spaceLayoutPreference = null;
  return pref;
}

export function setSpaceLayoutPreference(val) {
  spaceLayoutPreference = val;
}

export function openMyBoard() {
  manualSetSpaceLayout({
    value: MY_BOARD,
  });
}

export function pinnedShareEnlarge(luid) {
  if (selectGotMyBearings(getState())) enlargeScreen(luid);
}

export function enlargeScreen(luid) {
  if (getState().spaceMedia.people[luid]?.share)
    manualSetSpaceLayout({
      value: PINNED_LAYOUT,
      luid: luid,
      media: SHARE,
    });
}
export function enlargeCamera(luid) {
  if (getState().spaceMedia.people[luid]?.camOn)
    manualSetSpaceLayout({
      value: PINNED_LAYOUT,
      luid: luid,
      media: CAMERA,
    });
}

// Will enlarge first element of media array if it exists and has a video running
export function tryEnlargeFirst() {
  if (selectMeFocus(getState())) turnOffFocus();
  let firstVidLuid = getState().spaceMedia.firstVidLuid;
  if (firstVidLuid) {
    let pPerson = getState().spaceMedia.people[firstVidLuid];
    if (!pPerson) return;
    if (pPerson.share)
      manualSetSpaceLayout({
        value: PINNED_LAYOUT,
        luid: firstVidLuid,
        media: SHARE,
      });
    else if (pPerson.camOn && !(!pPerson.inRange && pPerson.shout === "share"))
      manualSetSpaceLayout({
        value: PINNED_LAYOUT,
        luid: firstVidLuid,
        media: CAMERA,
      });
  }
}

export function tryOpenGrid() {
  if (selectMeFocus(getState())) turnOffFocus();
  if (getState().spaceMedia.runningVideos > 1) {
    manualSetSpaceLayout({ value: GRID_LAYOUT });
    return true;
  }
  return false;
}

export function openToSpaceLayout() {
  if (selectSpaceLayoutValue(getState()) === MY_BOARD) makeMyTease();
  manualSetSpaceLayout({ value: SPACE_LAYOUT });
}

//#endregion

//#region ------   MEDIA LOOP
// DETERMINING ALL CHANGES TO BATCH UPDATE

let mediaLoopRef = null;
export function startMediaLoop() {
  mediaLoopRef = setInterval(handleSpaceChanges, MEDIA_LOOP_INTERVAL);
  if ((testingMyMovement || testingOtherMovement) && !testStarted) startTest();
}

export function stopMediaLoop() {
  if (mediaLoopRef) clearInterval(mediaLoopRef);
}

export function forceHandleSpaceChanges() {
  handleSpaceChanges();
}

function handleSpaceChanges() {
  let newPeople = gatherAnyNewPeopleChanges();
  let deviceChanges = gatherAnyDeviceChanges();
  let leftPeople = gatherAnyLeftPeopleChanges();
  let inRangeArray = gatherAnyInRangeChanges();
  let spaceLayoutPreference = gatherAnySpaceLayoutPreferenceChanges();

  if (
    deviceChanges ||
    inRangeArray ||
    newPeople ||
    leftPeople ||
    spaceLayoutPreference
  )
    dispatch(
      setSpaceChanges({
        deviceChanges,
        inRangeArray,
        newPeople,
        leftPeople,
        spaceLayoutPreference,
      })
    );
}

//#endregion

export default slice.reducer;
